import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useTrocaGestor } from '@/features/gestor/hooks/useTrocaGestor';
import type { Gestor } from '@/services/Auth/types';

function formatCnpj(value?: string): string {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length !== 14) return value || 'N/A';
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

interface GestorSwitchModalProps {
  /** Modo dropdown do header: controla abertura/fechamento do Dialog. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Modo gate (`RequireGestor`): sem Dialog, não pode ser fechado, auto-seleciona se só há 1 gestor. */
  obrigatorio?: boolean;
}

export function GestorSwitchModal({
  open,
  onOpenChange,
  obrigatorio = false,
}: GestorSwitchModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const { gestores, gestorAtualCnpj, carregando, erro, trocando, erroTroca, trocar } =
    useTrocaGestor(obrigatorio || open, obrigatorio);

  const gestorAtual = gestores.find(g => g.CNPJ_Gestor === gestorAtualCnpj) ?? null;

  const gestoresFiltrados = gestores.filter(gestor => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase().trim();
    const nome = (gestor.Nome || '').toLowerCase();
    const cnpj = (gestor.CNPJ_Gestor || '').replace(/\D/g, '');
    const searchDigits = search.replace(/\D/g, '');
    return nome.includes(search) || (!!searchDigits && cnpj.includes(searchDigits));
  });

  const conteudo = (
    <div className="mt-4 space-y-4">
      {gestorAtual && (
        <div className="rounded-lg border bg-muted/20 p-4">
          <div className="text-sm font-semibold">Gestor atual</div>
          <div className="mt-2 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{gestorAtual.Nome || 'N/A'}</div>
              <div className="text-xs text-muted-foreground">
                CNPJ: {formatCnpj(gestorAtual.CNPJ_Gestor)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-sm font-semibold">Gestores disponíveis</div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar por nome ou CNPJ..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        {erroTroca && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {erroTroca}
          </div>
        )}

        {carregando ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Carregando gestores...
          </div>
        ) : erro ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Não foi possível carregar os gestores vinculados.
          </div>
        ) : gestoresFiltrados.length === 0 ? (
          <div className="rounded-lg border bg-background p-8 text-center">
            <div className="text-sm text-muted-foreground">
              {searchTerm
                ? `Nenhum gestor encontrado para "${searchTerm}"`
                : 'Nenhum gestor disponível.'}
            </div>
          </div>
        ) : (
          <div className="max-h-[45vh] overflow-auto rounded-lg border bg-background">
            {gestoresFiltrados.map((gestor: Gestor) => {
              const isAtual = gestor.CNPJ_Gestor === gestorAtualCnpj;
              const isTrocando = trocando === gestor.CNPJ_Gestor;
              return (
                <div
                  key={gestor.CNPJ_Gestor}
                  className="flex items-start gap-3 border-b bg-background p-3 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{gestor.Nome}</div>
                    <div className="text-xs text-muted-foreground">
                      CNPJ: {formatCnpj(gestor.CNPJ_Gestor)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={isAtual ? 'secondary' : 'default'}
                    className="shrink-0"
                    disabled={!!trocando || isAtual}
                    onClick={() => trocar(gestor)}
                  >
                    {isAtual ? 'Selecionado' : isTrocando ? 'Selecionando...' : 'Selecionar'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!obrigatorio && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!trocando}>
            Fechar
          </Button>
        </div>
      )}
    </div>
  );

  if (obrigatorio) {
    return (
      <div className="w-full max-w-md text-left">
        <div className="mb-4 text-center">
          <h2 className="text-lg font-semibold text-foreground">Selecione um gestor</h2>
          <p className="text-sm text-muted-foreground">
            Sua conta está vinculada a mais de um gestor. Escolha um para continuar.
          </p>
        </div>
        {conteudo}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[calc(100%-2rem)] p-6 sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Trocar gestor</DialogTitle>
          <DialogDescription>
            Selecione um gestor para trocar o contexto das consultas e documentos.
          </DialogDescription>
        </DialogHeader>
        {conteudo}
      </DialogContent>
    </Dialog>
  );
}
